import { OutputData } from '@editorjs/editorjs';

interface EditorJSBlock {
  type: string;
  data: any;
}

function parseTableElement(tableElement: HTMLTableElement): EditorJSBlock | null {
  const rows = Array.from(tableElement.querySelectorAll('tr'));
  if (rows.length === 0) return null;
  const content: string[][] = [];
  let withHeadings = false;
  const firstRowCells = rows[0].querySelectorAll('th, td');
  if (firstRowCells.length > 0 && firstRowCells[0].tagName.toLowerCase() === 'th') {
    withHeadings = true;
  }
  rows.forEach(row => {
    const cells = Array.from(row.querySelectorAll('th, td'));
    const rowData = cells.map(cell => cell.textContent?.trim() || '');
    if (rowData.length > 0) content.push(rowData);
  });
  if (content.length === 0) return null;
  return { type: 'table', data: { withHeadings, content } };
}

export function htmlToEditorJS(html: string): OutputData {
  if (isEditorJSData(html)) {
    try { return JSON.parse(html); } catch {/* ignore */}
  }

  const blocks: EditorJSBlock[] = [];

  const pushParagraph = (textOrHtml: string) => {
    const content = (textOrHtml || '').trim();
    if (!content) return;
    // Split on two or more newlines to create separate paragraphs
    const segments = content.split(/\n{2,}/).filter(Boolean);
    segments.forEach(seg => {
      // Convert single \n to <br> within paragraphs to preserve line breaks
      const textWithBreaks = seg.replace(/\n/g, '<br>');
      blocks.push({ type: 'paragraph', data: { text: textWithBreaks } });
    });
  };

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      // Preserve single newlines, only collapse multiple spaces
      const t = (node.textContent || '').replace(/[ \t]+/g, ' ').trim();
      if (t) pushParagraph(t);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();
    switch (tag) {
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6': {
        const headerText = el.textContent?.trim() || '';
        if (headerText) blocks.push({ type: 'header', data: { text: headerText, level: parseInt(tag.charAt(1)) } });
        return;
      }
      case 'ul':
      case 'ol': {
        const items = Array.from(el.children)
          .filter(c => (c as HTMLElement).tagName.toLowerCase() === 'li')
          .map(li => (li as HTMLElement).textContent?.trim() || '')
          .filter(Boolean);
        if (items.length > 0) blocks.push({ type: 'list', data: { style: tag === 'ul' ? 'unordered' : 'ordered', items } });
        return;
      }
      case 'table': {
        const t = parseTableElement(el as HTMLTableElement);
        if (t) blocks.push(t);
        return;
      }
      case 'img': {
        const img = el as HTMLImageElement;
        const src = img.getAttribute('src') || img.src;
        if (src) blocks.push({ type: 'image', data: { file: { url: src }, caption: img.alt || img.title || '', withBorder: false, withBackground: false, stretched: false } });
        return;
      }
      case 'br': {
        blocks.push({ type: 'paragraph', data: { text: '' } });
        return;
      }
      case 'p': {
        // Preserve inline HTML inside paragraph (e.g., <br>)
        pushParagraph(el.innerHTML || el.textContent || '');
        return;
      }
      default: {
        // Container elements: walk children to avoid losing nested content
        if (el.children.length === 0) {
          pushParagraph(el.innerHTML || el.textContent || '');
          return;
        }
        Array.from(el.childNodes).forEach(walk);
        return;
      }
    }
  };

  if (html.includes('<') && html.includes('>')) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const body = doc.body;
    const nodes = Array.from(body.childNodes);
    if (nodes.length === 0) {
      pushParagraph(html.replace(/<[^>]*>/g, ''));
    } else {
      nodes.forEach(walk);
    }
  } else {
    pushParagraph(html);
  }

  if (blocks.length === 0) {
    blocks.push({ type: 'paragraph', data: { text: 'Commencez à taper...' } });
  }
  return { time: Date.now(), blocks, version: '2.30.8' };
}

export function editorJSToHtml(data: OutputData): string {
  if (!data.blocks || data.blocks.length === 0) return '';
  const htmlParts: string[] = [];
  data.blocks.forEach(block => {
    if (!block || !block.type || !block.data) return;
    switch (block.type) {
      case 'paragraph':
        if (block.data.text) htmlParts.push(`<p>${block.data.text}</p>`);
        break;
      case 'header': {
        const level = block.data.level || 1;
        if (block.data.text) htmlParts.push(`<h${level}>${block.data.text}</h${level}>`);
        break;
      }
      case 'list': {
        const listTag = block.data.style === 'ordered' ? 'ol' : 'ul';
        const items = block.data.items || [];
        if (items.length > 0) {
          const listItems = items.map((item: any) => {
            const itemText = typeof item === 'string' ? item : (item?.content || item?.text || String(item));
            return `<li>${itemText}</li>`;
          }).join('');
          htmlParts.push(`<${listTag}>${listItems}</${listTag}>`);
        }
        break;
      }
      case 'image': {
        const imageUrl = block.data.file?.url || block.data.url;
        const caption = block.data.caption || '';
        if (imageUrl) htmlParts.push(`<img src="${imageUrl}" alt="${caption}" />`);
        break;
      }
      case 'table': {
        const content = block.data.content || [];
        if (content.length > 0) {
          const withHeadings = block.data.withHeadings;
          let tableHtml = '<table>';
          content.forEach((row: string[], index: number) => {
            const cellTag = withHeadings && index === 0 ? 'th' : 'td';
            const cells = row.map(cell => `<${cellTag}>${cell}</${cellTag}>`).join('');
            tableHtml += `<tr>${cells}</tr>`;
          });
          tableHtml += '</table>';
          htmlParts.push(tableHtml);
        }
        break;
      }
      case 'code':
        if (block.data.code) htmlParts.push(`<pre><code>${block.data.code}</code></pre>`);
        break;
      default:
        if (block.data && block.data.text) htmlParts.push(`<p>${block.data.text}</p>`);
        break;
    }
  });
  return htmlParts.join('\n');
}

export function isEditorJSData(value: string): boolean {
  if (!value || typeof value !== 'string') return false;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && Array.isArray(parsed.blocks) && parsed.blocks.every((block: any) => block && typeof block === 'object' && typeof block.type === 'string' && block.data !== undefined);
  } catch {
    return false;
  }
}


